/**
 * Component: sa-hero-scroll
 * Description: Hero section with video intro and scroll-controlled image sequence (Sticky + Container Scroll)
 */
if (!customElements.get('sa-hero-scroll')) {
  class SaHeroScroll extends HTMLElement {
    constructor() {
      super();
      this.render = this.render.bind(this);
      this.playhead = { frame: 0 };
      this.frames = []; // Stores frame objects { img: Image, loaded: boolean, src: string }
      this.videoFinished = false;
      this.textStates = [];
      this.scrollProgress = 0;
      this.lastRenderedIndex = -1;
      this.currentTargetIndex = 0;
    }

    connectedCallback() {
      setTimeout(() => this.init(), 100);
    }

    disconnectedCallback() {
      if (this.scrollTrigger) this.scrollTrigger.kill();
      if (this.videoTrigger && this.handleTriggerClick) {
        this.videoTrigger.removeEventListener('click', this.handleTriggerClick);
      }
      if (this.previewClose && this.handleCloseClick) {
        this.previewClose.removeEventListener('click', this.handleCloseClick);
      }
      if (this.previewBackdrop && this.handleBackdropClick) {
        this.previewBackdrop.removeEventListener('click', this.handleBackdropClick);
      }
      if (this.handleKeyDown) {
        window.removeEventListener('keydown', this.handleKeyDown);
      }
    }

    async init() {
      
      // Elements
      this.video = this.querySelector('.sa-hero-scroll__video');
      this.videoContainer = this.querySelector('.sa-hero-scroll__video-container');
      this.banner = this.querySelector('.sa-hero-scroll__banner');
      this.heroText = this.querySelector('.sa-hero-txt');
      this.gJson = this.querySelector('.sa-hero-scroll__g-json');
      this.frameContainer = this.querySelector('.sa-hero-scroll__frame-container');
      this.currentImg = this.querySelector('.sa-hero-scroll__current-img');
      this.frameTexts = Array.from(this.querySelectorAll('.sa-frame-txt'));
      this.videoTrigger = this.querySelector('.sa-video-trigger');

      // Configuration
      this.imageBaseUrl = this.getAttribute('data-image-base-url');
      this.totalFrames = parseInt(this.getAttribute('data-total-frames')) || 603;
      
      // Video Events
      if (this.video) {
        // Prevent black screen: Fade in when ready
        const onVideoReady = () => {
             this.video.style.opacity = 1;
        };

        if (this.video.readyState >= 3) {
            onVideoReady();
        } else {
            this.video.addEventListener('loadeddata', onVideoReady);
            this.video.addEventListener('canplay', onVideoReady);
        }

        // Handle video finish
        this.video.addEventListener('ended', () => {
            this.videoFinished = true;
            this.updateBannerState();
        });
      }

      // Initialize frames array
      for (let i = 1; i <= this.totalFrames; i++) {
        this.frames.push({
            src: `${this.imageBaseUrl}${i}.png`,
            loaded: false,
            img: null // Image object will be created during preload
        });
      }

      // Initial render of the first frame (even if hidden)
      if (this.frames.length > 0) {
         this.currentImg.src = this.frames[0].src;
      }

      if (this.frameContainer) {
        this.frameContainer.style.display = 'none';
      }

      // Wait for GSAP
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        const checkGsap = setInterval(() => {
          if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            clearInterval(checkGsap);
            this.start();
          }
        }, 100);
      } else {
        this.start();
      }
      
      // Initialize text frames state (independent windows)
      if (this.frameTexts && this.frameTexts.length) {
        gsap.set(this.frameTexts, { opacity: 0, y: 100 });
        this.textStates = new Array(this.frameTexts.length).fill(false);
        // Visibility windows (progress ranges) for txt1/2/3
        this.textWindows = [
          { in: 0.08, out: 0.33 },
          { in: 0.42, out: 0.78 },
          { in: 0.86, out: 0.99 }
        ];
      }

      // Video preview trigger
      if (this.videoTrigger) {
        this.handleTriggerClick = (e) => {
          e.preventDefault();
          const url = this.videoTrigger.getAttribute('data-video-url');
          if (!url) return;
          this.openPreview(url);
        };
        this.videoTrigger.addEventListener('click', this.handleTriggerClick);
      }
    }

    start() {
      gsap.registerPlugin(ScrollTrigger);
      
      // 1. Setup ScrollTrigger
      this.setupScrollAnimation();
      
      // 2. Preload Images (Lazy Strategy)
      // Only start loading when user interacts or video finishes
      
      const startPreload = () => {
          if (this.preloadStarted) return;
          this.preloadStarted = true;
          this.preloadImages();
      };

      // Trigger 1: Video Finishes
      if (this.video) {
          this.video.addEventListener('ended', startPreload, { once: true });
      }

      // Trigger 2: User Scrolls (Interaction)
      window.addEventListener('scroll', startPreload, { once: true, passive: true });
      
      // Trigger 3: Fallback Timer (Late Load)
      // If user just stares at the video for 8 seconds, start loading slowly in background
      setTimeout(startPreload, 8000);
    }

    preloadImages() {
      // Priority Loading Strategy:
      // 1. Load keyframes (every 10th frame) first to ensure rough scrubbing works quickly
      // 2. Load the rest sequentially
      
      const loadFrame = (index) => {
          if (index >= this.frames.length || this.frames[index].loaded || this.frames[index].img) return;

          const frame = this.frames[index];
          const img = new Image();
          frame.img = img; // Keep reference
          img.src = frame.src;
          
          img.onload = () => {
              frame.loaded = true;
          };
          img.onerror = () => {
              frame.loaded = true; // Mark as processed even if failed to avoid infinite retry
          };
      };

      // Create a priority list
      const priorityList = [];
      const stride = 10; // Load every 10th frame first

      // Ensure first and last frames are prioritized
      priorityList.push(0);
      priorityList.push(this.totalFrames - 1);

      // Pass 1: Keyframes (0, 10, 20...)
      for (let i = 0; i < this.totalFrames; i += stride) {
          priorityList.push(i);
      }

      // Pass 2: The rest
      for (let i = 0; i < this.totalFrames; i++) {
          if (i % stride !== 0) {
              priorityList.push(i);
          }
      }

      // Process the queue with a batch limit to avoid freezing the browser
      // Reduced batch size to 4 to prevent main thread blocking during video playback
      const batchSize = 4;
      let currentIndex = 0;

      const processBatch = () => {
          // If video is playing, throttle loading further
          // If video is playing, throttle loading SIGNIFICANTLY (1 image at a time) to protect bandwidth
          const effectiveBatchSize = (!this.videoFinished && !this.video.paused) ? 1 : batchSize;

          const end = Math.min(currentIndex + effectiveBatchSize, priorityList.length);
          
          for (let i = currentIndex; i < end; i++) {
              loadFrame(priorityList[i]);
          }

          currentIndex += effectiveBatchSize;
          
          if (currentIndex < priorityList.length) {
              // Use requestIdleCallback if available, otherwise requestAnimationFrame or setTimeout
              if (window.requestIdleCallback) {
                  // If video is playing, request a much longer timeout/idle period
                  const timeoutVal = (!this.videoFinished && !this.video.paused) ? 2000 : 1000;
                  window.requestIdleCallback(processBatch, { timeout: timeoutVal });
              } else {
                  // Increased delay to 50ms to be gentler, 100ms if video playing
                  const delay = (!this.videoFinished && !this.video.paused) ? 100 : 50;
                  setTimeout(processBatch, delay);
              }
          }
      };

      processBatch();
    }

    preloadAround(index, radius = 6) {
      const start = Math.max(0, index - radius);
      const end = Math.min(this.totalFrames - 1, index + radius);
      for (let i = start; i <= end; i++) {
        const frame = this.frames[i];
        if (!frame) continue;
        if (frame.loaded || frame.img) continue;
        const img = new Image();
        frame.img = img;
        img.src = frame.src;
        img.onload = () => { frame.loaded = true; };
        img.onerror = () => { frame.loaded = true; };
      }
    }

    updateBannerState() {
        // Toggle banner image
        if (this.banner) {
          if (this.videoFinished) {
              this.banner.classList.add('sa-hero-scroll__banner--visible');
          } else {
              this.banner.classList.remove('sa-hero-scroll__banner--visible');
          }
        }

        // Toggle hero text visibility and trigger animations
        if (this.heroText) {
          if (this.videoFinished) {
            this.heroText.classList.add('sa-hero-txt--visible');
          } else {
            this.heroText.classList.remove('sa-hero-txt--visible');
          }
        }
    }

    showVideo() {
        if (this.videoContainer && this.videoHidden) {
            this.videoHidden = false;
            this.videoContainer.classList.remove('sa-hero-scroll__video-container--hidden');
            
            this.updateBannerState();

            if (this.video) {
                if (!this.videoFinished) {
                    this.video.play();
                } else {
                    this.video.pause();
                }
            }
            
            if (this.frameContainer) {
                this.frameContainer.style.display = 'none';
            }
        }
    }

    hideVideo() {
      if (this.videoContainer && !this.videoHidden) {
        this.videoHidden = true;
        this.videoContainer.classList.add('sa-hero-scroll__video-container--hidden');
        if (this.video) this.video.pause();
        
        if (this.frameContainer) {
            this.frameContainer.style.display = '';
        }
        if (this.gJson) {
            this.gJson.style.display = '';
        }
        
        // Render current frame immediately
        this.render();
      }
    }

    setupScrollAnimation() {
      this.scrollTrigger = gsap.to(this.playhead, {
        frame: this.totalFrames - 1,
        snap: "frame",
        ease: "none",
        scrollTrigger: {
          trigger: this,
          start: "top top",
          end: "bottom bottom",
          scrub: 1,
          onUpdate: (self) => {
            this.scrollProgress = self.progress;
            const total = this.totalFrames - 1;
            const idx = Math.min(total, Math.max(0, Math.floor(self.progress * total)));
            this.currentTargetIndex = idx;
            this.preloadAround(idx, 6);
            // Video/Image Visibility Logic
            if (self.progress <= 0.005) {
                if (this.videoHidden) this.showVideo();
            } else {
                if (!this.videoHidden) this.hideVideo();
            }
            
            this.render();
            this.updateTextFrames(self.progress, self.direction);
          }
        }
      });
      
      ScrollTrigger.refresh();
    }

    render() {
      const targetIndex = this.currentTargetIndex ?? Math.round(this.playhead.frame);
      if (targetIndex === this.lastRenderedIndex) return;
      const frame = this.frames[targetIndex];
      const url = frame?.src;
      if (this.currentImg && url && this.currentImg.src !== url) {
        this.currentImg.src = url;
        this.lastRenderedIndex = targetIndex;
      }
    }

    updateTextFrames(progress, direction) {
      // Only animate text frames when the video is hidden (scroll phase)
      if (!this.frameTexts || !this.frameTexts.length) return;
      if (!this.videoHidden) {
        // Hide all when video visible
        for (let i = 0; i < this.frameTexts.length; i++) {
          if (this.textStates[i]) {
            this.deactivateText(i, -1); // hide downward when switching back to video
            this.textStates[i] = false;
          }
        }
        return;
      }

      // Independent visibility windows per text block
      for (let i = 0; i < this.frameTexts.length; i++) {
        const win = this.textWindows?.[i];
        if (!win) continue;
        const isLast = i === this.frameTexts.length - 1;
        const nowVisible = isLast ? (progress >= win.in) : (progress >= win.in && progress <= win.out);
        const wasVisible = this.textStates[i] === true;
        if (nowVisible && !wasVisible) {
          this.activateText(i, direction);
          this.textStates[i] = true;
        } else if (!nowVisible && wasVisible) {
          this.deactivateText(i, direction);
          this.textStates[i] = false;
        }
      }
    }

    activateText(index, direction) {
      const el = this.frameTexts[index];
      if (!el) return;
      el.classList.add('is-active');
      const isMobile = window.innerWidth <= 768;
      const targetY = isMobile ? 0 : -200;
      const fromY = direction === -1 ? -100 : 100; // up-scroll enters from above; down-scroll enters from below
      gsap.fromTo(el, { opacity: 0, y: fromY }, { opacity: 1, y: targetY, duration: 1.2, ease: 'power1.out', overwrite: 'auto' });
    }

    deactivateText(index, direction) {
      const el = this.frameTexts[index];
      if (!el) return;
      el.classList.remove('is-active');
      const toY = direction === 1 ? -300 : 100; // scrolling down hides upward; scrolling up hides downward
      gsap.to(el, { opacity: 0, y: toY, duration: 0.8, ease: 'power1.out', overwrite: 'auto' });
    }

    createPreview() {
      if (this.previewEl) return;
      const overlay = document.createElement('div');
      overlay.className = 'sa-hero-scroll__video-preview';
      overlay.setAttribute('aria-hidden', 'true');

      const backdrop = document.createElement('div');
      backdrop.className = 'sa-hero-scroll__video-preview-backdrop';

      const content = document.createElement('div');
      content.className = 'sa-hero-scroll__video-preview-content';

      const closeBtn = document.createElement('button');
      closeBtn.className = 'sa-hero-scroll__video-preview-close';
      closeBtn.setAttribute('type', 'button');
      closeBtn.setAttribute('aria-label', 'Close');

      const video = document.createElement('video');
      video.className = 'sa-hero-scroll__video-preview-player';
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('controls', '');
      video.setAttribute('preload', 'metadata');

      content.appendChild(closeBtn);
      content.appendChild(video);
      overlay.appendChild(backdrop);
      overlay.appendChild(content);
      this.appendChild(overlay);

      this.previewEl = overlay;
      this.previewVideo = video;
      this.previewClose = closeBtn;
      this.previewBackdrop = backdrop;

      this.handleCloseClick = (e) => {
        e.preventDefault();
        this.closePreview();
      };
      this.handleBackdropClick = (e) => {
        e.preventDefault();
        this.closePreview();
      };
      this.handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closePreview();
        }
      };

      this.previewClose.addEventListener('click', this.handleCloseClick);
      this.previewBackdrop.addEventListener('click', this.handleBackdropClick);
      window.addEventListener('keydown', this.handleKeyDown);
    }

    openPreview(url) {
      this.createPreview();
      if (!this.previewEl || !this.previewVideo) return;
      if (this.video) this.video.pause();
      this.previewVideo.src = url;
      this.previewEl.classList.add('sa-hero-scroll__video-preview--open');
      this.previewEl.setAttribute('aria-hidden', 'false');
      this.previewVideo.play().catch(() => {});
    }

    closePreview() {
      if (!this.previewEl || !this.previewVideo) return;
      this.previewVideo.pause();
      this.previewVideo.removeAttribute('src');
      this.previewVideo.load();
      this.previewEl.classList.remove('sa-hero-scroll__video-preview--open');
      this.previewEl.setAttribute('aria-hidden', 'true');
    }
  }

  customElements.define('sa-hero-scroll', SaHeroScroll);
}
